import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ColumnDef } from '@tanstack/react-table'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { format } from 'date-fns'
import { Badge } from '@/components/shadcn/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { Button } from '@/components/shadcn/button'
import { toast } from 'sonner'

type DatabricksMessagesDatabricksJobRun = RestSchemas['DatabricksMessages.DatabricksJobRun']

export const runsTableColumns: ColumnDef<DatabricksMessagesDatabricksJobRun>[] = [
  {
    id: 'runId',
    accessorKey: 'runId',
    header: ({ column }) => <SortingHeaderButton column={column}>Run ID</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="flex flex-col">
        {row.original.runUrl ? (
          <a href={row.original.runUrl} target="_blank" rel="nofollow">
            {row.original.runId}
          </a>
        ) : (
          <div>{row.original.runId}</div>
        )}
        <span className="text-xs text-muted-foreground">{row.original.jobName}</span>
      </div>
    ),
  },

  {
    id: 'startTime',
    accessorKey: 'startTime',
    header: ({ column }) => <SortingHeaderButton column={column}>Start - End</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span>
          <span className="font-bold">Start - </span> {format(row.original.startTime as string, 'PP (p)')}
        </span>
        <span>
          <span className="font-bold">End - </span>{' '}
          {row.original.endTime ? format(row.original.endTime as string, 'PP (p)') : 'N/A'}
        </span>
      </div>
    ),
  },

  {
    id: 'state',
    accessorKey: 'state',
    header: 'State',
    cell: ({ row }) => (
      <Badge variant="outline" className="font-normal">
        {row.original.state}
      </Badge>
    ),
  },
  {
    id: 'result',
    accessorKey: 'result',
    header: 'Result',
    cell: ({ row }) => {
      const badgeVariant =
        row.original.result === 'SUCCESS' ? 'success' : row.original.result === 'FAILED' ? 'error' : 'default'
      return <Badge variant={badgeVariant}>{row.original.result}</Badge>
    },
  },
  {
    id: 'context',
    accessorKey: 'context',
    header: 'Context',
    cell: ({ row }) =>
      row.original.context ? (
        <code className="block max-w-[200px] truncate rounded bg-muted px-1 text-xs" title={row.original.context}>
          {row.original.context}
        </code>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex justify-end pr-5">
        <RowActionsMenu run={row.original} />
      </div>
    ),
  },
]

function RowActionsMenu({ run }: { run: DatabricksMessagesDatabricksJobRun }) {
  const { postDatabricksApiRefreshRunStatus, getDatabricksApiListJobRuns } = useRestClient()

  const invalidateRuns = () => getDatabricksApiListJobRuns.invalidateQueries()

  const { mutate: refreshRun } = postDatabricksApiRefreshRunStatus.useMutation()

  const onRefreshRun = (runId: number) => {
    refreshRun(
      { path: { runId: runId } },
      {
        onError: (error) => {
          toast.error('Error refreshing job run: ' + getDatabricksErrorMessage(error))
        },
        onSuccess: () => {},
        onSettled: () => {
          invalidateRuns()
        },
      }
    )
  }

  const onDownloadLogs = () => {
    if (run.logs) {
      const byteArray = new Uint8Array(run.logs.length)

      for (let i = 0; i < run.logs.length; i++) {
        byteArray[i] = run.logs.charCodeAt(i)
      }

      const blob = new Blob([byteArray], { type: 'application/octet-stream' })
      const objectUrl = window.URL.createObjectURL(blob)
      const filename = `logs-${run.jobName}-${run.startTime}.txt`

      const anchor = document.createElement('a')
      document.body.appendChild(anchor)
      anchor.setAttribute('href', objectUrl)
      anchor.setAttribute('download', filename)
      anchor.click()
      window.URL.revokeObjectURL(objectUrl)
    } else {
      toast.error('No logs available')
    }
  }

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          // This is necessary to maintain the open state when we want to keep it open
          if (!open) {
            // Check if we need to prevent closing
            // This is a placeholder for custom logic you might need
            // We'll use other mechanisms to prevent closing on item click
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="size-8 rounded-full">
            <IconMenuOutline size={16} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => {
            // Prevent focus returning to trigger which can close the menu
            e.preventDefault()
          }}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => onRefreshRun(run.runId)}>Refresh</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onDownloadLogs()}>Download Logs</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
